import { useEffect, useState } from "react";
import { api, STATUS_COLORS } from "../api";
import { useAuth } from "../context/AuthContext";
import { useLang, fmtRM, fmtDate } from "../i18n";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Separator } from "../components/ui/separator";
import PayslipModal from "../components/PayslipModal";
import { LogIn, LogOut, Eye, Umbrella } from "lucide-react";
import { toast } from "sonner";

export default function MyPortal() {
  const { user, employee, tenant } = useAuth();
  const { lang } = useLang();
  const bm = lang === "bm";
  const [balances, setBalances] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [today, setToday] = useState(null);
  const [slip, setSlip] = useState(null);

  const load = () => {
    api.get("/leave/balances").then((r) => setBalances(r.data)).catch(() => {});
    api.get("/payroll/payslips").then((r) => setPayslips(r.data)).catch(() => {});
    api.get("/attendance/today").then((r) => setToday(r.data?.attendance_id ? r.data : null)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const clock = async (type) => {
    try {
      await api.post("/attendance/clock", { type });
      toast.success("OK");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  return (
    <div data-testid="my-portal" className="space-y-6">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{bm ? "Portal Saya" : "My Portal"}</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-1">
          <CardHeader><CardTitle className="text-base">{bm ? "Profil" : "Profile"}</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-2">
            <p className="font-semibold text-base">{employee?.full_name || user?.name}</p>
            <p className="text-slate-500">{employee?.position || user?.role}</p>
            <Separator />
            {[
              [bm ? "ID Pekerja" : "Employee ID", employee?.emp_no],
              [bm ? "Jabatan" : "Department", employee?.department],
              [bm ? "Cawangan" : "Branch", employee?.branch],
              [bm ? "Status" : "Status", employee?.status],
              [bm ? "Tarikh Sertai" : "Join Date", fmtDate(employee?.join_date)],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between">
                <span className="text-slate-500">{k}</span>
                <span className="font-medium">{v || "-"}</span>
              </div>
            ))}
            <Separator />
            <div className="flex gap-2 pt-1">
              <Button data-testid="portal-clock-in-button" size="sm" className="bg-emerald-600 hover:bg-emerald-700 flex-1"
                disabled={!!today?.clock_in || !employee} onClick={() => clock("in")}>
                <LogIn className="w-4 h-4 mr-1" /> Clock In
              </Button>
              <Button data-testid="portal-clock-out-button" size="sm" variant="outline" className="flex-1"
                disabled={!today?.clock_in || !!today?.clock_out} onClick={() => clock("out")}>
                <LogOut className="w-4 h-4 mr-1" /> Clock Out
              </Button>
            </div>
            {today?.clock_in && (
              <p className="text-xs text-slate-500 text-center">
                {bm ? "Masuk" : "In"}: {new Date(today.clock_in).toLocaleTimeString("en-MY", { hour: "2-digit", minute: "2-digit" })}
                {today.clock_out && ` · ${bm ? "Keluar" : "Out"}: ${new Date(today.clock_out).toLocaleTimeString("en-MY", { hour: "2-digit", minute: "2-digit" })}`}
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Umbrella className="w-4 h-4 text-blue-800" />{bm ? "Baki Cuti" : "Leave Balances"}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {balances.slice(0, 5).map((b) => (
              <div key={b.balance_id}>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{b.leave_type}</span>
                  <span className="font-mono font-semibold">{Math.max(0, b.entitled - b.used)}/{b.entitled}</span>
                </div>
                <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-700 rounded-full" style={{ width: `${Math.min(100, (b.used / b.entitled) * 100)}%` }} />
                </div>
              </div>
            ))}
            {balances.length === 0 && <p className="text-sm text-slate-500">-</p>}
          </CardContent>
        </Card>

        <Card className="lg:col-span-1">
          <CardHeader><CardTitle className="text-base">{bm ? "Payslip Terkini" : "Latest Payslips"}</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {payslips.slice(0, 4).map((p) => (
              <button key={p.payslip_id} data-testid={`my-payslip-${p.period}`} onClick={() => setSlip(p)}
                className="w-full flex items-center justify-between border rounded-lg px-3 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left">
                <div>
                  <p className="font-mono font-semibold text-sm">{p.period}</p>
                  <p className="text-xs text-slate-500">{bm ? "Bersih" : "Net"}: {fmtRM(p.net_pay)}</p>
                </div>
                <Eye className="w-4 h-4 text-slate-400" />
              </button>
            ))}
            {payslips.length === 0 && <p className="text-sm text-slate-500">{bm ? "Tiada payslip lagi." : "No payslips yet."}</p>}
          </CardContent>
        </Card>
      </div>
      <PayslipModal payslip={slip} open={!!slip} onClose={() => setSlip(null)} company={tenant?.company_name} />
    </div>
  );
}
